/**
 * 主题应用引擎（阶段一）
 * ============================================================
 * 职责：按 客户(corpId)/学段(position) 解析目标主题，把 token 注入
 * :root CSS 变量 + 切换 body class + 广播品牌信息。
 *
 * 阶段二接入点：把 THEME_CONFIG 换成后台接口下发即可，本引擎不改。
 *
 * CSS 变量映射（语义 token → 实际变量名）：
 *   写「兼容名」维持存量样式引用（--hud-cyan / --hud-amber ...），
 *   同时写「语义别名」供新代码使用（--hud-primary / --hud-accent ...）。
 *   派生变量（线条/软底/辉光）在 hud-theme.css 里以
 *   rgba(var(--hud-primary-rgb), α) 形式自动跟随主色，无需逐个配置。
 */
import { THEME_CONFIG, ALL_THEME_CLASSES } from './themeConfig'

// 2026-09-23 用户下线「配色配置」功能（原 loadCustomTheme/saveCustomTheme/setUserTheme
// 与 resolveTheme 的用户手选分支一并移除）；applyTheme 启动时清掉存量用户手选键，
// 防老用户 localStorage 残留继续压过客户/年级自动路由。

/** 语义 token → 输出 CSS 变量名列表（兼容名 + 语义名） */
const TOKEN_VAR_MAP = {
    void:            ['--hud-void'],
    panel:           ['--hud-panel'],
    panelDeep:       ['--hud-panel-deep'],
    panelHead:       ['--hud-panel-head'],
    primary:         ['--hud-cyan', '--hud-primary'],
    primaryRgb:      ['--hud-cyan-rgb', '--hud-primary-rgb'],
    primaryDeep:     ['--hud-primary-deep'],
    primaryDeepRgb:  ['--hud-primary-deep-rgb'],
    primaryBright:   ['--hud-primary-bright'],
    onPrimary:       ['--hud-on-primary'],
    accent:          ['--hud-amber', '--hud-accent'],
    accentRgb:       ['--hud-amber-rgb', '--hud-accent-rgb'],
    success:         ['--hud-green', '--hud-success'],
    danger:          ['--hud-red', '--hud-danger'],
    text:            ['--hud-text'],
    textDim:         ['--hud-text-dim'],
    textBright:      ['--hud-text-bright'],
    nebula1:         ['--hud-nebula-1'],
    nebula2:         ['--hud-nebula-2'],
    nebula3:         ['--hud-nebula-3'],
    starsOpacity:    ['--hud-stars-opacity']
};

/** 当前生效主题（供品牌区等读取） */
let currentTheme = null;

/** 按路由规则解析主题：URL ?theme= 强制 > corpId 精确 > position > 默认 */
export function resolveTheme(corpId, position) {
    const cfg = THEME_CONFIG;
    // 实施调试通道：?theme=<themeId> 强制指定（上线演示/现场排障用）
    try {
        const forced = new URLSearchParams(window.location.search).get('theme');
        if (forced && cfg.themes[forced]) return cfg.themes[forced];
    } catch (e) {}
    for (const r of cfg.routes) {
        if (r.corpId !== undefined && corpId !== undefined && Number(r.corpId) === Number(corpId)) {
            return cfg.themes[r.themeId] || cfg.themes[cfg.defaultTheme];
        }
        if (r.position !== undefined && position !== undefined && Number(r.position) === Number(position)) {
            return cfg.themes[r.themeId] || cfg.themes[cfg.defaultTheme];
        }
    }
    return cfg.themes[cfg.defaultTheme];
}

/**
 * 应用主题（幂等，可反复调用）。
 * 数据源：localStorage.zhimingLoginData.corp_id + localStorage.zhimingPosition
 * 调用时机：main.js 启动 / 登录成功 / 学段或组织变化（SelectLeft）
 */
export function applyTheme() {
    let corpId, position;
    try {
        corpId = (JSON.parse(localStorage.getItem('zhimingLoginData') || '{}') || {}).corp_id;
    } catch (e) { corpId = undefined; }
    try {
        position = Number(localStorage.getItem('zhimingPosition')) || undefined;
    } catch (e) { position = undefined; }
    // 清掉已下线「配色配置」的存量手选键，防老用户残留继续压过客户/年级自动路由
    try {
        localStorage.removeItem('zhimingUserTheme');
        localStorage.removeItem('zhimingCustomTheme');
    } catch (e) {}

    const theme = resolveTheme(corpId, position);
    const root = document.documentElement;

    // 1. 注入 token → CSS 变量
    for (const [key, vars] of Object.entries(TOKEN_VAR_MAP)) {
        if (theme.tokens[key] === undefined) continue;
        for (const v of vars) root.style.setProperty(v, theme.tokens[key]);
    }

    // 2. 切换 body class（移旧挂新）
    document.body.classList.remove(...ALL_THEME_CLASSES);
    document.body.classList.add(theme.themeClass);

    // 3. 记录当前主题并广播（品牌区、需要主题联动的地方监听）
    const changed = !currentTheme || currentTheme.themeId !== theme.themeId;
    currentTheme = theme;
    localStorage.setItem('zhimingThemeId', theme.themeId);
    if (changed && typeof window !== 'undefined' && window.__themeBus) {
        window.__themeBus(theme);
    }
    return theme;
}

/** 读取当前主题（未应用过时先解析一次） */
export function getCurrentTheme() {
    if (!currentTheme) {
        let corpId, position;
        try { corpId = (JSON.parse(localStorage.getItem('zhimingLoginData') || '{}') || {}).corp_id; } catch (e) {}
        try { position = Number(localStorage.getItem('zhimingPosition')) || undefined; } catch (e) {}
        currentTheme = resolveTheme(corpId, position);
    }
    return currentTheme;
}

/** 实时预览自定义 token（不持久化，仅注入当前页面） */
export function previewCustomTheme(tokens) {
    const theme = {
        themeId: 'custom-preview',
        themeClass: 'theme-custom',
        brand: { productName: '知明慧学 · 学生端', subName: 'MY CONSOLE', logoUrl: '' },
        tokens
    };
    const root = document.documentElement;
    for (const [key, vars] of Object.entries(TOKEN_VAR_MAP)) {
        if (theme.tokens[key] === undefined) continue;
        for (const v of vars) root.style.setProperty(v, theme.tokens[key]);
    }
    document.body.classList.remove(...ALL_THEME_CLASSES);
    document.body.classList.add(theme.themeClass);
    currentTheme = theme;
    return theme;
}

/** 主题变化订阅（简单总线，避免引入额外依赖） */
export function onThemeChange(fn) {
    window.__themeBus = fn;
}
